/**
 * Referral tracking helpers.
 * ClientProvider captures trusted path/query referrals into a short-lived cookie.
 * /sign-up reads the cookie and passes the result to Clerk unsafeMetadata.
 */

import { LANDING_PAGE_SLUGS } from "@/lib/analytics/events";

// Re-export for convenience — canonical definition is in storage-keys.ts
export { SS_REFERRAL_ID } from "@/lib/storage-keys";

/** Default referral ID for organic signups (no landing page, no ?src=). */
export const DEFAULT_REFERRAL_ID = "lev-signup";

/** Referral ID for users who sign up from a project collaboration invitation. */
export const PROJECT_COLLABORATION_INVITE_REFERRAL_ID = "project-collaboration-invite";

/** Referral ID for founders signing up from the public YC application funnel. */
export const YC_PUBLIC_APP_REFERRAL_ID = "yc_public_app";

/** First-party cookie that carries the best-known referral into Clerk sign-up. */
export const REFERRAL_COOKIE_NAME = "lev_referral_id";

/** Two weeks is long enough for signup handoff without becoming long-lived attribution state. */
export const REFERRAL_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 14;

const MAX_REFERRAL_ID_LENGTH = 64;
const MISSING_REFERRAL_SENTINELS = new Set(["none", "null", "undefined"]);
export const TRUSTED_LANDING_REFERRAL_IDS = [
  ...LANDING_PAGE_SLUGS.filter((slug) => slug !== "home"),
  "yc",
] as const;

const TRUSTED_LANDING_REFERRAL_ID_SET = new Set<string>(TRUSTED_LANDING_REFERRAL_IDS);

/**
 * Normalize and sanitize a referral ID.
 * Trims, lowercases, strips unsafe characters, caps length.
 * Returns empty string if input is invalid.
 */
export function normalizeReferralId(raw: string): string {
  const normalized = raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "");
  return normalized.slice(0, MAX_REFERRAL_ID_LENGTH);
}

export function normalizeMaybeReferralId(raw?: string | null): string | null {
  const normalized = normalizeReferralId(raw ?? "");
  if (MISSING_REFERRAL_SENTINELS.has(normalized)) return null;
  return normalized || null;
}

export function referralIdFromSignupQuery(args: {
  src?: string | null;
  referralId?: string | null;
  ycClaimSource?: string | null;
}): string | null {
  const src = normalizeMaybeReferralId(args.src);
  if (src) return src;
  const referralId = normalizeMaybeReferralId(args.referralId);
  if (referralId) return referralId;
  return args.ycClaimSource === YC_PUBLIC_APP_REFERRAL_ID
    ? YC_PUBLIC_APP_REFERRAL_ID
    : null;
}

export function buildYcPublicAppSignupHref(
  args: {
    email?: string | null;
    inviteToken?: string | null;
  } = {},
): string {
  const redirectUrl = args.inviteToken
    ? `/yc/claim-callback?inviteToken=${encodeURIComponent(args.inviteToken)}`
    : "/yc/claim-callback";
  const params = new URLSearchParams({
    src: YC_PUBLIC_APP_REFERRAL_ID,
    redirect_url: redirectUrl,
    yc_claim_source: YC_PUBLIC_APP_REFERRAL_ID,
  });
  const email = args.email?.trim();
  if (email) {
    params.set("email_address_hint", email);
  }
  return `/sign-up?${params.toString()}`;
}

export function buildAuthRedirectHref(
  pathname: "/sign-in" | "/sign-up",
  args: {
    redirectUrl?: string | null;
    email?: string | null;
    /** LEV-1773: partner slug forwarded so Clerk attribution survives a missing cookie. */
    src?: string | null;
  } = {},
): string {
  const params = new URLSearchParams();
  const redirectUrl = args.redirectUrl?.trim();
  const email = args.email?.trim();
  const src = args.src?.trim();
  if (redirectUrl) params.set("redirect_url", redirectUrl);
  if (email) params.set("email_address_hint", email);
  if (src) params.set("src", src);

  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}

export function hrefWithSearchParams(
  pathname: string,
  query: Record<string, string | string[] | undefined>,
): string {
  const qp = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value == null) continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        qp.append(key, item);
      }
    } else {
      qp.set(key, value);
    }
  }
  const queryString = qp.toString();
  return queryString.length > 0 ? `${pathname}?${queryString}` : pathname;
}

export function resolveReferralIdForCapture(args: {
  storedReferralId?: string | null;
  pathname?: string | null;
  src?: string | null;
  referralId?: string | null;
}): string | null {
  const storedReferralId = normalizeMaybeReferralId(args.storedReferralId);
  if (storedReferralId) return storedReferralId;

  const pathReferralId = referralIdFromPath(args.pathname ?? "");
  if (pathReferralId) return pathReferralId;

  return (
    normalizeMaybeReferralId(args.src) ?? normalizeMaybeReferralId(args.referralId)
  );
}

export function resolveReferralIdForSignup(args: {
  storedReferralId?: string | null;
  src?: string | null;
  referralId?: string | null;
  ycClaimSource?: string | null;
}): string {
  const storedReferralId = normalizeMaybeReferralId(args.storedReferralId);
  if (storedReferralId) return storedReferralId;

  return (
    referralIdFromSignupQuery({
      src: args.src,
      referralId: args.referralId,
      ycClaimSource: args.ycClaimSource,
    }) ?? DEFAULT_REFERRAL_ID
  );
}

export function readReferralCookie(cookieSource?: string | null): string | null {
  const source =
    cookieSource ?? (typeof document === "undefined" ? "" : document.cookie);
  const prefix = `${REFERRAL_COOKIE_NAME}=`;

  for (const chunk of source.split(";")) {
    const cookie = chunk.trim();
    if (!cookie.startsWith(prefix)) continue;
    const value = cookie.slice(prefix.length);
    try {
      return normalizeMaybeReferralId(decodeURIComponent(value));
    } catch {
      /* benign: best-effort URL decoding of cookie value */
      return normalizeMaybeReferralId(value);
    }
  }

  return null;
}

export function writeReferralCookie(referralId: string): void {
  if (typeof document === "undefined") return;

  const normalized = normalizeMaybeReferralId(referralId);
  if (!normalized) return;
  const secure = typeof window !== "undefined" && window.location.protocol === "https:";

  document.cookie = [
    `${REFERRAL_COOKIE_NAME}=${encodeURIComponent(normalized)}`,
    "Path=/",
    `Max-Age=${REFERRAL_COOKIE_MAX_AGE_SECONDS}`,
    "SameSite=Lax",
    secure ? "Secure" : "",
  ]
    .filter(Boolean)
    .join("; ");
}

/**
 * Derive a referral ID from a landing page pathname.
 * Uses only trusted first path segments so raw paths cannot mint arbitrary referrals.
 */
export function referralIdFromPath(pathname: string): string {
  const firstSegment = pathname.replace(/^\//, "").split("/")[0];
  const referralId = normalizeReferralId(firstSegment);
  return TRUSTED_LANDING_REFERRAL_ID_SET.has(referralId) ? referralId : "";
}
